import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowRight, AlertCircle } from 'lucide-react';
import logger from '../utils/logger';

const ActivationCodeVerification = () => {
  const navigate = useNavigate();
  const [activationCode, setActivationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCodeChange = useCallback((e) => {
    setActivationCode(e.target.value.toUpperCase());
    setError('');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!activationCode.trim()) {
      setError('Please enter your activation code');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/verify-activation-code`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ activation_code: activationCode.trim() })
        }
      );

      const data = await response.json();

      if (response.ok) {
        // Store the verification token
        localStorage.setItem('verificationToken', data.verification_token);
        logger.info('Activation code verified successfully');

        // Redirect to signup page
        navigate('/signup');
      } else {
        setError(data.error || 'Invalid activation code');
        logger.warn('Activation code verification failed', { error: data.error });
      }
    } catch (err) {
      logger.error('Activation code verification error', { error: err });
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{
        backgroundColor: '#1f2937',
        backgroundImage: 'url(/Background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="flex justify-center mb-8">
        <img
          src="/Login Logo.png"
          alt="Dominion Business Advisors"
          className="h-40 w-auto"
          loading="eager"
          decoding="async"
          style={{ contentVisibility: 'auto' }}
        />
      </div>

      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Shield className="text-blue-600" size={32} />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Verify Activation Code</h1>
            <p className="text-gray-600">
              Enter the activation code provided by your advisor to continue with registration
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label htmlFor="activationCode" className="block text-sm font-medium text-gray-700 mb-2">
                Activation Code
              </label>
              <input
                type="text"
                id="activationCode"
                value={activationCode}
                onChange={handleCodeChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg font-mono tracking-wider"
                placeholder="ENTER-CODE-HERE"
                maxLength={50}
                disabled={loading}
                autoFocus
              />
              <p className="mt-2 text-xs text-gray-500">
                Don't have an activation code? Contact your advisor to request one.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !activationCode.trim()}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Verifying...
                </>
              ) : (
                <>
                  Continue to Sign Up
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Already have an account? Sign In
            </button>
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-gray-400">
          <p>Secured by Dominion Business Advisors</p>
        </div>
      </div>
    </div>
  );
};

export default ActivationCodeVerification;
